import { FulfillmentType } from "JS/types/ProductDelivery";
import { ILocalStoreJson } from "JS/types/LocalStore";

/** Result of a function which validates product quantity. returns false if there's no error, or a string error message otherwise */
export type ProductQtyValidationResult = string | false;

export interface HasQtyInterval {
	qtyInterval?: number | null;
}

export interface HasQtyMin {
	qtyMin?: number | null;
}

export interface HasQtyMax {
	qtyMax?: number | null;
}

export interface HasStock {
	storeEcomOnHand?: number | null;
	shipEcomOnHand?: number | null;
}

export function calculateMinBuyableQty({ qtyInterval, qtyMin }: HasQtyMin & HasQtyInterval) {
	let min = 1;
	if (qtyInterval !== null) min = qtyInterval;
	if (qtyMin !== null) min = qtyMin;
	return min;
};

export function calculateMaxBuyableQty(fulfillmentType: FulfillmentType, localStore: ILocalStoreJson, product: HasStock) {
	if (fulfillmentType === FulfillmentType.StandardShipping) {
		return product.shipEcomOnHand;
	} else if (localStore !== null) {
		return product.storeEcomOnHand;
	}

	return null;
};

export function isQuantityGreaterThanBuyable(quantity: number, fulfillmentType: FulfillmentType, localStore: ILocalStoreJson, product: HasStock): boolean {
	return quantity > calculateMaxBuyableQty(fulfillmentType, localStore, product);
}

export function validateQuantityUnderMaxBuyable(quantity: number, fulfillmentType: FulfillmentType, localStore: ILocalStoreJson, product: HasStock): ProductQtyValidationResult {
	return isQuantityGreaterThanBuyable(quantity, fulfillmentType, localStore, product)
		? `Only ${calculateMaxBuyableQty(fulfillmentType, localStore, product)} left`
		: false;
}

export function isQuantityLessThanProductMin(quantity: number, product: HasQtyMin): boolean {
	return product.qtyMin && quantity < product.qtyMin;
}

export function validateQuantityOverProductMin(quantity: number, product: HasQtyMin): ProductQtyValidationResult {
	return isQuantityLessThanProductMin(quantity, product)
		? product.qtyMin + ' item minimum'
		: false;
}

export function isQuantityOutsideInterval(quantity: number, product: HasQtyInterval): boolean {
	return product.qtyInterval && quantity % product.qtyInterval !== 0;
}

export function validateQuantityWithinInterval(quantity: number, product: HasQtyInterval): ProductQtyValidationResult {
	if (isQuantityOutsideInterval(quantity, product)) {
		return 'Quantity must be a multiple of ' + product.qtyInterval;
	}

	return false;
}

export function isQuantityGreaterThanMax(quantity: number, product: HasQtyMax): boolean {
	return product.qtyMax && quantity > product.qtyMax;
}

export function validateQuantityUnderProductMax(quantity: number, product: HasQtyMax): ProductQtyValidationResult {
	if (isQuantityGreaterThanMax(quantity, product)) {
		return product.qtyMax + ' item maximum';
	}

	return false;
}

export function validateQuantity(quantity: number, product: HasQtyMax & HasQtyMin & HasQtyInterval) {
	return validateQuantityOverProductMin(quantity, product)
		|| validateQuantityWithinInterval(quantity, product)
		|| validateQuantityUnderProductMax(quantity, product);
}

export function getSpecialQuantityMessaging({ qtyMin, qtyInterval }: HasQtyMin & HasQtyInterval): string | null {
	if (!qtyMin && !qtyInterval) {
		return null;
	}

	if (qtyMin && qtyInterval) {
		return `${qtyMin} item minimum. Must be purchased in multiples of ${qtyInterval}.`;
	} else if (qtyMin && !qtyInterval) {
		return `${qtyMin} item minimum.`;
	} else {
		return `Must be purchased in multiples of ${qtyInterval}.`;
	}
}


